import { Link, Navigate } from 'react-router-dom';
import {
  CalendarDays,
  Factory,
  Landmark,
  Megaphone,
  Package,
  ShieldCheck,
  ShoppingCart,
  Users,
} from 'lucide-react';
import { Logo } from '@/components/brand/Logo';
import { Button } from '@/components/ui/Button';
import { useAuth } from '@/app/AuthProvider';

/**
 * Public Atlas homepage. Hosted at `/` and reachable without authentication
 * so Google OAuth brand verification (and any other external reviewer) can
 * confirm two things from a single URL:
 *
 *   1. The application name on this page is **Atlas** — matching the name
 *      on the Google Cloud OAuth consent screen.
 *   2. The page explains the purpose of Atlas (what it does, who it is
 *      for, and why it uses Google accounts) rather than being a login
 *      screen only.
 *
 * Signed-in users are forwarded into the app so `/` still behaves as the
 * post-auth landing it used to be.
 */
export function HomePage() {
  const { session, loading, role } = useAuth();

  // Show the public page while auth is resolving so crawlers never land on
  // a spinner. Only bounce once we know there is a session.
  if (!loading && session) {
    if (role === 'external_lender') {
      return <Navigate to="/financials/loans/family" replace />;
    }
    return <Navigate to="/dashboard" replace />;
  }

  return <PublicHome />;
}

const MODULES = [
  {
    icon: Landmark,
    title: 'Financials',
    body: 'Bank accounts, transactions, actuals, projections, reconciliation, expenses, and loans — Atlas is Virro’s financial source of truth.',
  },
  {
    icon: Factory,
    title: 'Manufacturing',
    body: 'Bills of materials, work centers, work orders, MRP, and shop-floor quality live in Atlas so production planning stays connected to inventory and cost.',
  },
  {
    icon: Package,
    title: 'Inventory & procurement',
    body: 'On-hand stock, locations, movements, purchase orders, goods receipts, and vendor invoices — Atlas tracks what Virro owns and what it owes.',
  },
  {
    icon: ShoppingCart,
    title: 'Sales',
    body: 'Orders, quotes, channel connections, shipments, and returns. Atlas is where Virro records and fulfills customer demand.',
  },
  {
    icon: Megaphone,
    title: 'Marketing',
    body: 'Web analytics, advertising, social, affiliates, and competitive tracking. Atlas pulls marketing performance into the same operating picture as sales.',
  },
  {
    icon: Users,
    title: 'People & projects',
    body: 'PTO, org chart, reviews, handbook, complaints, and project work. Atlas is also where Virro coordinates the calendar and internal delivery.',
  },
  {
    icon: ShieldCheck,
    title: 'Quality / ISO',
    body: 'Controlled documents, training, calibration, supplier qualification, NCR/CAPA, and audits — Atlas supports Virro’s quality system.',
  },
  {
    icon: CalendarDays,
    title: 'Calendar',
    body: 'Team scheduling in Atlas, with optional Google Calendar connection so authorized users can see and manage work alongside their Google calendars.',
  },
] as const;

function PublicHome() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="border-b border-border bg-card">
        <div className="mx-auto flex max-w-5xl items-center justify-between gap-4 px-6 py-4">
          <Link to="/" className="flex items-center gap-3" aria-label="Atlas">
            <Logo variant="primary" tone="humus" className="h-7" />
            <span className="text-sm font-medium tracking-headline">Atlas</span>
          </Link>
          <nav className="flex items-center gap-4 text-xs font-medium text-muted-foreground">
            <Link to="/legal/privacy" className="hover:text-foreground">
              Privacy Policy
            </Link>
            <Link to="/legal/eula" className="hover:text-foreground">
              EULA
            </Link>
            <Link to="/auth/sign-in" className="hover:text-foreground">
              Sign in
            </Link>
          </nav>
        </div>
      </header>

      <main>
        <section className="border-b border-border bg-humus text-linen">
          <div className="mx-auto max-w-5xl px-6 py-16 md:py-24">
            <p className="text-xs font-medium uppercase tracking-wide text-linen/60">
              Virro internal software
            </p>
            <h1 className="mt-3 text-5xl font-medium tracking-headline leading-headline md:text-6xl">
              Atlas
            </h1>
            <p className="mt-6 max-w-2xl text-lg leading-body text-linen/80">
              Atlas is Virro&rsquo;s internal enterprise resource planning
              (ERP) application. Atlas exists so authorized Virro employees
              and contractors can run the company from one system: financial
              truth, manufacturing depth, inventory, sales, marketing, people
              operations, and quality — together.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button asChild size="lg" className="bg-card text-foreground hover:bg-linen">
                <Link to="/auth/sign-in">Sign in to Atlas</Link>
              </Button>
              <Button
                asChild
                size="lg"
                variant="outline"
                className="border-linen/30 text-linen hover:bg-linen/10 hover:text-linen"
              >
                <Link to="/legal/privacy">Privacy Policy</Link>
              </Button>
            </div>
          </div>
        </section>

        <section className="mx-auto max-w-5xl px-6 py-16">
          <h2 className="text-2xl font-medium tracking-headline leading-headline">
            The purpose of Atlas
          </h2>
          <div className="mt-6 max-w-3xl space-y-4 text-sm leading-body text-muted-foreground">
            <p>
              <strong className="font-medium text-foreground">Atlas</strong>{' '}
              is not a consumer product and is not offered to the public.
              Atlas is the operating system for Virro (Eloo LLC d/b/a Virro
              Life). Its purpose is to give people who work at Virro a
              single, permissioned place to record, review, and act on the
              information that runs the business.
            </p>
            <p>
              Before Atlas, financials, production, inventory, sales
              channels, and marketing lived in separate tools. Atlas
              replaces that scatter: an employee signs in, sees the modules
              their role allows, and works against one ledger — quotes to
              cash, purchase orders to stock, work orders to shipments,
              marketing performance next to actuals.
            </p>
            <p>
              Access to Atlas is limited to Virro personnel and authorized
              contractors who have been provisioned an account. Visitors
              without an Atlas account cannot use the application; they can
              read this homepage, the{' '}
              <Link
                to="/legal/privacy"
                className="text-atmosphere-dark underline-offset-4 hover:underline"
              >
                Atlas Privacy Policy
              </Link>
              , and the{' '}
              <Link
                to="/legal/eula"
                className="text-atmosphere-dark underline-offset-4 hover:underline"
              >
                Atlas End-User License Agreement
              </Link>
              .
            </p>
          </div>
        </section>

        <section className="border-y border-border bg-card">
          <div className="mx-auto max-w-5xl px-6 py-16">
            <h2 className="text-2xl font-medium tracking-headline leading-headline">
              What Atlas does
            </h2>
            <p className="mt-3 max-w-3xl text-sm leading-body text-muted-foreground">
              Atlas is a full internal ERP. Each area below is a product
              surface inside Atlas, gated by role so people only see what
              they need to do their job.
            </p>
            <ul className="mt-10 grid gap-6 sm:grid-cols-2">
              {MODULES.map((mod) => (
                <li key={mod.title} className="flex gap-4">
                  <mod.icon
                    className="mt-0.5 h-5 w-5 shrink-0 text-old-growth"
                    aria-hidden
                  />
                  <div>
                    <h3 className="text-sm font-medium tracking-headline">
                      {mod.title}
                    </h3>
                    <p className="mt-1 text-sm leading-body text-muted-foreground">
                      {mod.body}
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </section>

        <section className="mx-auto max-w-5xl px-6 py-16">
          <h2 className="text-2xl font-medium tracking-headline leading-headline">
            How Atlas uses Google accounts
          </h2>
          <div className="mt-6 max-w-3xl space-y-4 text-sm leading-body text-muted-foreground">
            <p>
              Atlas uses Google so Virro employees can sign in with the
              Google Workspace account they already have, and so Atlas can
              connect to Google products Virro already runs the business on.
              Atlas does not use Google accounts to offer a public
              consumer app.
            </p>
            <ul className="list-disc space-y-3 pl-5">
              <li>
                <span className="font-medium text-foreground">Sign-in.</span>{' '}
                Authorized users sign in to Atlas with their Virro Google
                Workspace identity. Atlas uses this to authenticate the
                person, create or resume their Atlas session, and apply
                their role-based permissions. Atlas does not allow
                self-service public registration.
              </li>
              <li>
                <span className="font-medium text-foreground">
                  Google Calendar.
                </span>{' '}
                Users who choose to connect a calendar can view and
                coordinate Atlas work alongside their Google Calendar.
              </li>
              <li>
                <span className="font-medium text-foreground">
                  Google Drive and Sheets.
                </span>{' '}
                When you save a bill of materials, Atlas signs you in with
                Google and asks you to pick a Drive folder and a title. Atlas
                creates that spreadsheet in the folder you chose.
              </li>
              <li>
                <span className="font-medium text-foreground">
                  Google marketing properties.
                </span>{' '}
                Administrators can connect Google Analytics, Google Ads,
                Search Console, and YouTube so Atlas can show Virro&rsquo;s
                own marketing and site performance next to sales.
              </li>
            </ul>
            <p>
              How Atlas accesses, uses, stores, and shares Google user data
              is described in the{' '}
              <Link
                to="/legal/privacy"
                className="text-atmosphere-dark underline-offset-4 hover:underline"
              >
                Atlas Privacy Policy
              </Link>
              . Atlas uses Google user data only to operate and improve
              these Atlas features for the signed-in Virro user, not to sell
              data or to serve ads.
            </p>
          </div>
        </section>
      </main>

      <footer className="border-t border-border">
        <div className="mx-auto flex max-w-5xl flex-col gap-3 px-6 py-8 text-xs text-muted-foreground sm:flex-row sm:items-center sm:justify-between">
          <p>
            &copy; {new Date().getFullYear()} Eloo LLC d/b/a Virro Life.
            Atlas is internal-use software.
          </p>
          <nav className="flex gap-4">
            <Link to="/legal/privacy" className="hover:text-foreground">
              Privacy Policy
            </Link>
            <Link to="/legal/eula" className="hover:text-foreground">
              EULA
            </Link>
            <Link to="/auth/sign-in" className="hover:text-foreground">
              Sign in to Atlas
            </Link>
          </nav>
        </div>
      </footer>
    </div>
  );
}
